/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { IMediaImageModel, ThemeContext } from "@bms/common";
import cx from "classnames";
import { FocusedImage } from "image-focus";
import * as React from "react";

import { useImageLoader } from "hooks/useImageLoader";

import { LoaderSpinner } from "../LoaderSpinner";

import "./ImageWithPlaceholder.scss";

export interface IImageWithPlaceholderProps {
  imageContainerClassName?: string; // custom className for component
  imageContainerStyles?: React.CSSProperties; // custom styles for component
  imageClassName?: string; // custom styles for image element
  fallbackImageSrc?: string; // source for the image if imageSrc returns 404
  spinner?: boolean;
  gradient?: string;
  placeholderSrc?: string;
  imageData?: IMediaImageModel;
  disableAbsolutePosition?: boolean;
  placeholderClassName?: string;
}

/*
 * Component which will display given image based od imageSrc. Until image is not loaded (ready to be rendered)
 * a placeholder image will be rendered
 */
export const ImageWithPlaceholder = ({
  imageClassName,
  fallbackImageSrc,
  gradient,
  spinner,
  placeholderSrc,
  imageData,
  disableAbsolutePosition,
  placeholderClassName,
}: IImageWithPlaceholderProps) => {
  const { themeProvider } = React.useContext(ThemeContext);
  const [mounted, setMounted] = React.useState(false);
  const [loaded, setLoaded] = React.useState<boolean>(false);
  const [result, setResult] = React.useState<string | null>(null);
  const { loadImage } = useImageLoader();
  const imageElem = React.useRef<HTMLImageElement>(null);

  React.useEffect(() => {
    if (
      !imageData ||
      imageData.FocalPointX === undefined ||
      imageData.FocalPointY === undefined ||
      !loaded
    ) {
      return;
    }

    if (!imageElem.current) {
      return;
    }

    const _focusInstance = new FocusedImage(imageElem.current, {
      focus: { x: imageData.FocalPointX || 0, y: imageData.FocalPointY || 0 },
    });
  }, [imageData, loaded]);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  React.useEffect(() => {
    if (imageData?.Url) {
      setLoaded(false);
    }
  }, [imageData?.Url]);

  React.useEffect(() => {
    if (imageData?.Url && !loaded && mounted) {
      loadImage(imageData.Url, fallbackImageSrc).then((result) => {
        setLoaded(!!result);
        setResult(result);
      });
    }
  }, [imageData?.Url, loaded, mounted]);

  if (loaded) {
    return (
      <img
        alt="placeholder"
        key="placeholder"
        draggable={false}
        src={result || imageData?.Url}
        className={cx("ImageWithPlaceholder-img", imageClassName, {
          WithoutFocal: !imageData?.FocalPointX && !disableAbsolutePosition,
        })}
        ref={imageElem}
        data-focus-x="0"
        data-focus-y="0"
      />
    );
  } else if (!gradient && placeholderSrc && spinner) {
    return (
      <div key="spinner" className="ImageWithPlaceholder-spinner">
        <LoaderSpinner />
      </div>
    );
  }

  return (
    <div
      className={cx("ImagePlaceholder", placeholderClassName, {
        WithoutFocal: !disableAbsolutePosition,
      })}
      style={{
        backgroundImage: `url(${placeholderSrc})`,
        backgroundColor: themeProvider.getColor("AppCellsBackgroundColor"),
      }}
    />
  );
};
